import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from 'hooks';
import Menu from 'components/menu';
import Table from 'components/table';
import styles from './capacities.module.scss';
import Select from '../../components/ui/form/select';
import { ProductionCapacityService, ProductService } from '../../services';
import Input from '../../components/ui/form/input';

const CAPACITY_MIN = 0;
const CAPACITY_MAX = 2147483647;
const CAPACITY_MONTH_FIELDS = ['jan', 'feb', 'mar', 'apr', 'may', 'june', 'july', 'aug', 'sept', 'oct', 'nov', 'dec'];
const CAPACITY_VALID_INPUT_RE = /^\d+$/;

const Capacities = () => {
  const { setUser, setLoading } = useStore();
  const [generalTotal, setGeneralTotal] = useState(0);
  const [productionCapacity, setProductionCapacity] = useState(null);
  const [products, setProducts] = useState([]);
  const [currentProductId, setCurrentProductId] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const productResponse = await new ProductService({ setUser }).getProducts({ page: 0, limit: 1000 });
      const list = Array.isArray(productResponse)
        ? productResponse
        : (productResponse?.items ?? []);
      setProducts(list);
    })();
  }, [setUser, setLoading]);

  const onChangeProduct = async (e) => {
    const { value } = e.target;
    if (value) {
      setLoading(true);
      const productionCapacitiesResponse = await new ProductionCapacityService({ setUser })
        .getCapacities(value);

      setCurrentProductId(value);
      if (productionCapacitiesResponse) {
        setProductionCapacity(productionCapacitiesResponse);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const generalTotalResponse = await new ProductionCapacityService({ setUser })
        .getGeneralTotal();
      if (generalTotalResponse) {
        setGeneralTotal(generalTotalResponse);
      }
      setLoading(false);
    })();
  }, [products, setUser, productionCapacity, setLoading]);

  const normalizeCapacityValue = useCallback((rawValue) => {
    const numericValue = Number(rawValue);
    if (!Number.isFinite(numericValue)) {
      return CAPACITY_MIN;
    }

    return Math.min(CAPACITY_MAX, Math.max(CAPACITY_MIN, Math.trunc(numericValue)));
  }, []);

  const normalizeCapacitiesPayload = useCallback((payload) => {
    const normalizedPayload = { ...payload };
    CAPACITY_MONTH_FIELDS.forEach((field) => {
      normalizedPayload[field] = normalizeCapacityValue(payload[field]);
    });

    return normalizedPayload;
  }, [normalizeCapacityValue]);

  const onChange = useCallback(async (value, month, inputType) => {
    let nextValue = value;

    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (trimmedValue === '') {
        const isDeletion = typeof inputType === 'string' && inputType.startsWith('delete');
        if (!isDeletion) {
          return;
        }
        nextValue = CAPACITY_MIN;
      } else if (!CAPACITY_VALID_INPUT_RE.test(trimmedValue)) {
        return;
      }
    }

    const normalizedValue = normalizeCapacityValue(nextValue);
    const changedValues = normalizeCapacitiesPayload({ ...productionCapacity, [month]: normalizedValue });
    const newProductionCapacitiesResponse = await new ProductionCapacityService({ setUser })
      .putCapacities(currentProductId, changedValues);

    if (newProductionCapacitiesResponse) {
      setProductionCapacity(newProductionCapacitiesResponse);
    }
  }, [currentProductId, normalizeCapacitiesPayload, normalizeCapacityValue, productionCapacity, setUser]);

  const cells = [
    {
      name: 'Январь',
      renderField: 'jan',
    }, {
      name: 'Февраль',
      renderField: 'feb',
    }, {
      name: 'Март',
      renderField: 'mar',
    }, {
      name: 'Апрель',
      renderField: 'apr',
    }, {
      name: 'Май',
      renderField: 'may',
    }, {
      name: 'Июнь',
      renderField: 'june',
    }, {
      name: 'Июль',
      renderField: 'july',
    }, {
      name: 'Август',
      renderField: 'aug',
    }, {
      name: 'Сентябрь',
      renderField: 'sept',
    }, {
      name: 'Октябрь',
      renderField: 'oct',
    }, {
      name: 'Ноябрь',
      renderField: 'nov',
    }, {
      name: 'Декабрь',
      renderField: 'dec',
    }, {
      name: 'Итог',
      renderField: 'total',
      nowrap: true,
    },
  ];

  const cellsWithRenderers = cells.map(el => ({
    ...el,
    onRender: (item, value, index, renderField) => renderField !== 'total' ? (
      <div>
        <Input
          type="number"
          controlsUnder
          min={CAPACITY_MIN}
          max={CAPACITY_MAX}
          step={1}
          onDecrement={() => { onChange(value - 1, renderField); }}
          onIncrement={() => { onChange(value + 1, renderField); }}
          onChange={({ target, nativeEvent }) => { onChange(target.value, renderField, nativeEvent?.inputType); }}
          value={value}
        />
      </div>
    ) : <p>{value}</p>,
  }));

  return (
    <>
      <div className={styles.menu}>
        <Menu />
      </div>
      <div className={styles.header}>
        <div>
          <Select onChange={onChangeProduct}>
            <option key="default" value="">Выбрать</option>
            {products.map(productItem => (
              <option value={productItem.id} key={productItem.id}>{productItem.name}</option>
            ))}
          </Select>
        </div>
        <div>
          Общая мощность:
          {' '}
          <b>{generalTotal}</b>
          {' '}
          тонн
        </div>
      </div>
      <div className={styles.table}>
        {productionCapacity ? (
          <Table
            cells={cellsWithRenderers}
            data={[productionCapacity]}
          />
        ) : null}
      </div>
    </>
  );
};

export default Capacities;
